import { type StoreApi } from "zustand/vanilla";
import { type VaultState } from "../model/vaultStore.ts";
import { type VaultAdapter } from "../vault/VaultAdapter.ts";
import { createDocStore, type DocState } from "../model/store.ts";
import { CanvasView } from "../render/CanvasView.ts";
import { MarkdownView, type MarkdownViewOptions } from "../edit/MarkdownView.ts";
import { PdfView } from "../edit/PdfView.ts";
import { toJSONCanvas, fromJSONCanvas } from "../vault/jsonCanvas.ts";
import { loadCamera, saveCamera } from "../vault/fsAccess.ts";
import { retargetPath } from "../vault/canvasRefs.ts";
import { linkTargets, makePathIndex, resolveHref, type PathIndex } from "../vault/links.ts";
import { emptyDoc, type VaultTreeNode } from "../model/types.ts";
import {
  ATTACHMENTS_DIR,
  imageCandidates,
  isExternalSrc,
  isPdfPath,
} from "../vault/attachments.ts";
import { readHighlights, writeHighlights } from "../vault/highlightStore.ts";

const SAVE_DEBOUNCE_MS = 400;

/**
 * Turn an image `src` from a markdown document into a renderable URL. Relative
 * paths resolve against the document's folder first (Obsidian behaviour), then
 * fall back to vault-root-relative. External URLs pass through untouched.
 */
async function resolveImage(
  adapter: VaultAdapter,
  docPath: string,
  src: string,
): Promise<string | null> {
  if (isExternalSrc(src)) return src;
  for (const candidate of imageCandidates(docPath, src)) {
    try {
      const blob = await adapter.readBinary(candidate);
      return URL.createObjectURL(blob);
    } catch {
      // not in this location — try the next candidate
    }
  }
  return null;
}

/**
 * Los lugares donde puede haber un documento montado.
 *
 * `main` es el área grande de siempre. `peek` es el panel lateral, para
 * consultar una nota sin soltar dónde estabas. El orden importa: es el que
 * recorren `flush()` y `closeAll()`, y el principal va primero.
 */
const SLOTS = ["main", "peek"] as const;

export type SlotId = (typeof SLOTS)[number];

type Entry =
  | {
      kind: "canvas";
      path: string;
      view: CanvasView;
      store: StoreApi<DocState>;
      unsub: () => void;
      timer: number | null;
      lastJson: string;
    }
  | { kind: "markdown"; path: string; view: MarkdownView }
  | { kind: "pdf"; path: string; view: PdfView };

type Slots = Record<SlotId, Entry | null>;

/**
 * Un lugar al que se puede volver.
 *
 * No es una ruta: es una ruta Y dónde se estaba parado adentro. Volver a un
 * documento y encontrarlo arriba de todo no es volver, es abrirlo de nuevo.
 *
 * El lienzo no guarda nada acá porque ya guarda de más: su cámara se persiste
 * por ruta (`saveCamera`) y `open` la repone sola.
 */
interface Place {
  path: string;
  hash: string;
  /** Desplazamiento de la hoja al momento de irse. Sólo markdown. */
  scroll: number;
}

/** El rastro de un slot: por dónde se pasó, y en qué paso se está parado. */
interface Trail {
  places: Place[];
  /** Índice del paso actual. -1 mientras no se abrió nada de este lado. */
  at: number;
}

/**
 * Tope de pasos recordados por slot. Un rastro es para deshacer un desvío, no
 * un registro de la sesión: sin tope crece toda la tarde sin que nadie lo mire.
 */
const TRAIL_MAX = 50;

function emptyTrail(): Trail {
  return { places: [], at: -1 };
}

/**
 * Owns the main document area. Opens one document per slot: a `.canvas` mounts
 * a CanvasView, a `.md` mounts a MarkdownView.
 *
 * Cada slot tiene UN documento vivo, pero el relevo se hace montando lo nuevo
 * encima y desarmando lo viejo abajo (ver la rama de markdown de `open`), no al
 * revés: el hueco entre las dos vistas se ve. Un canvas se desmonta apenas la
 * hoja está puesta, así que nunca hay dos contextos de Pixi dibujando a la vez.
 */
export interface WorkspaceHosts {
  /** Main area host where both canvas and markdown documents render. */
  host: HTMLElement;
  /** Host del panel lateral. Mientras no exista, el slot `peek` no monta nada. */
  peekHost?: HTMLElement;
  /** Empty-state shown in the main area when no document is open. */
  emptyState: HTMLElement;
  /** Text node inside `emptyState` naming the open vault's root folder. */
  emptyStateText: HTMLElement;
  /**
   * Switch the sidebar form factor: "floating" for canvas/empty (compact panel)
   * and "docked" for markdown (classic full-height left rail).
   */
  setSidebarLayout: (layout: "floating" | "docked") => void;
  /**
   * Mostrar el panel lateral titulado con `path`, o cerrarlo con `null`.
   *
   * Se llama ANTES de montar la vista: CodeMirror se mide contra el alto de su
   * contenedor al construirse, y armarlo dentro de un panel todavía escondido
   * lo deja midiendo cero.
   */
  setPeek?: (path: string | null) => void;
  /** Prender o apagar las flechas de ir y volver de un slot. */
  setTrail?: (slot: SlotId, canBack: boolean, canForward: boolean) => void;
  /**
   * Signal the current view mode so the UI can show/hide canvas-specific
   * affordances (e.g. the sidebar collapse/expand button). Lo dice SÓLO el slot
   * principal: ver `enterMain`.
   */
  setViewMode?: (mode: "canvas" | "markdown" | null) => void;
}

export class Workspace {
  private slots: Slots = { main: null, peek: null };
  /** Slot que atiende el teclado. Ver `setFocusedSlot`. */
  private focused: SlotId = "main";
  /** Por dónde pasó cada lado. Ver `Trail`. */
  private trails: Record<SlotId, Trail> = { main: emptyTrail(), peek: emptyTrail() };
  /**
   * Índice de rutas del vault, memorizado contra el árbol que lo originó.
   *
   * Resolver un enlace tiene que ser barato: un documento con cien enlaces se
   * resuelve entero en cada render (para pintar los rotos) y el buscador de
   * `[[` se recalcula en cada tecla. `setTree` reemplaza el arreglo entero, así
   * que comparar identidades alcanza para saber si el índice sigue vigente.
   */
  private indexedTree: VaultTreeNode[] | null = null;
  private index: PathIndex = new Map();
  private targets: string[] = [];

  constructor(
    private hosts: WorkspaceHosts,
    private vault: StoreApi<VaultState>,
  ) {}

  /** Refresca índice y lista de destinos si el árbol cambió desde la última vez. */
  private reindex(): void {
    const tree = this.vault.getState().tree;
    if (tree === this.indexedTree) return;
    this.indexedTree = tree;
    this.index = makePathIndex(tree);
    this.targets = linkTargets(tree);
  }

  /** Ruta del vault a la que apunta un enlace de `docPath`, o null. */
  private resolveLink(docPath: string, href: string): string | null {
    this.reindex();
    return resolveHref(docPath, href, this.index);
  }

  /** Rutas enlazables, para el buscador de `[[`. */
  private linkTargets(): string[] {
    this.reindex();
    return this.targets;
  }

  /**
   * Crea el documento que un enlace roto nombraba y devuelve su ruta.
   *
   * El tipo sale de la extensión que pedía el enlace: un `.canvas` nace como
   * canvas vacío y cualquier otra cosa como nota con su título puesto, igual
   * que si se lo hubiera creado desde la sidebar.
   */
  private async createLinked(path: string): Promise<string | null> {
    const adapter = this.vault.getState().adapter;
    if (!adapter) return null;
    const name = path.split("/").pop() ?? path;
    const stem = name.replace(/\.[^.]+$/, "");
    const contents = name.toLowerCase().endsWith(".canvas")
      ? toJSONCanvas(emptyDoc(name))
      : `# ${stem}\n\n`;
    try {
      const created = await adapter.create(path, contents);
      await this.vault.getState().refreshTree();
      return created;
    } catch (err) {
      console.error("create linked doc failed", path, err);
      return null;
    }
  }

  /**
   * Todo lo que una vista de markdown necesita del vault para un documento.
   *
   * Vive en un solo lugar porque hay DOS caminos que montan un documento —el
   * que reusa la vista abierta y el que la crea— y mientras cada uno armaba su
   * propio objeto, agregar una capacidad significaba acordarse de sumarla en
   * los dos; el que se olvidara quedaba sin ella y sólo al llegar por ese lado.
   *
   * `slot` viaja hasta `onNavigate` para que un enlace se abra donde vive el
   * documento que lo contiene: seguir un enlace desde el panel lateral navega
   * el panel, no el área grande.
   */
  private markdownOptions(
    adapter: VaultAdapter,
    path: string,
    text: string,
    slot: SlotId,
  ): Omit<MarkdownViewOptions, "highlights"> {
    return {
      title: path.split("/").pop() ?? path,
      docPath: path,
      initial: text,
      onSave: (contents) => adapter.write(path, contents),
      onSaveHighlights: (hs) => void writeHighlights(adapter, path, hs),
      saveImage: (blob, name) => adapter.createBinary(`${ATTACHMENTS_DIR}/${name}`, blob),
      resolveImage: (src) => resolveImage(adapter, path, src),
      resolveLink: (href) => this.resolveLink(path, href),
      // Un enlace se sigue DONDE vive el documento que lo contiene, salvo que
      // pida el costado: navegar desde el panel navega el panel.
      onNavigate: (target, hash, { peek }) =>
        void this.open(target, hash, { slot: peek ? "peek" : slot }),
      onCreateNote: (target) => this.createLinked(target),
      linkTargets: () => this.linkTargets(),
    };
  }

  /**
   * Dónde monta sus vistas cada slot.
   *
   * El peek contesta null mientras su panel no exista en el DOM: el modelo ya
   * sabe de los dos slots, la ventana todavía no, y pedirle que abra ahí tiene
   * que no hacer nada en vez de montar contra la nada.
   */
  private hostFor(slot: SlotId): HTMLElement | null {
    if (slot === "main") return this.hosts.host;
    return this.hosts.peekHost ?? null;
  }

  /**
   * Deja el slot listo para recibir una vista, y le pasa el teclado.
   *
   * El cromo del shell —la capa del sidebar, el modo de vista, el cartel de
   * vault vacío— lo manda SÓLO el principal: describe qué se está mirando en
   * grande. Si el panel lateral lo tocara, consultar una nota desde un lienzo
   * apagaría los controles propios del lienzo que sigue ahí, a la vista.
   */
  private enterSlot(slot: SlotId, mode: "canvas" | "markdown", path: string): void {
    // Abrir un documento en un lado es ponerse a trabajar de ese lado.
    this.focused = slot;
    if (slot === "peek") {
      this.hosts.setPeek?.(path);
      return;
    }
    this.hosts.emptyState.hidden = true;
    this.hosts.setSidebarLayout("docked");
    this.hosts.setViewMode?.(mode);
  }

  /**
   * Abre un documento en un slot. `hash` es el ancla (`#seccion`) de un enlace,
   * que se muestra recién después de montar la vista: sólo se puede saltar a un
   * encabezado que ya esté renderizado.
   *
   * Pedir el que YA está abierto en ese slot no rehace nada. Clickear en el
   * rail la fila del documento activo es un gesto de todos los días —y el
   * buscador ofrece el abierto como cualquier otro—, y rehacer la vista por eso
   * no es gratis: en un canvas es soltar el contexto de WebGPU y crear otro, que
   * se ve como un parpadeo negro del tamaño del lienzo.
   *
   * `reload` es para el único caso que sí necesita rehacerla: el archivo
   * cambió en disco bajo los pies de la vista (el barrido que reescribe los
   * enlaces después de un renombre) y hay que releerlo, o la vista seguiría
   * mostrando —y guardando— el texto viejo.
   */
  async open(
    path: string,
    hash = "",
    { slot = "main" as SlotId, reload = false, track = true } = {},
  ): Promise<void> {
    const adapter = this.vault.getState().adapter;
    if (!adapter) return;
    const host = this.hostFor(slot);
    if (!host) return;
    const isCanvas = path.endsWith(".canvas");

    // Los lugares no se peekean. Un lienzo tiene cámara y se vuelve a él: se
    // abre siempre en grande, y una nota se puede consultar al costado.
    //
    // No es una preferencia estética. Es lo que mantiene vivo UN solo contexto
    // de WebGPU sin que nadie tenga que contarlos: mientras el canvas sólo entre
    // por acá, no hay forma de llegar a dos.
    if (slot === "peek" && isCanvas) {
      await this.open(path, hash, { slot: "main", reload });
      return;
    }

    const active = this.slots[slot];
    if (!reload && active?.path === path) {
      // Un enlace a una sección de esta misma nota sí tiene algo que hacer.
      if (hash && active.kind === "markdown") active.view.revealAnchor(hash);
      return;
    }

    // Ocupación única: una ruta vive en UN slot como máximo. Dos vistas sobre
    // el mismo archivo son dos autoguardados con debounce escribiéndolo, y el
    // último en llegar pisa al otro sin que nada lo avise.
    const other = this.slotOf(path);
    if (other !== null && other !== slot) {
      // Pedirlo al costado cuando ya está en grande no duplica nada: ya se está
      // mirando, y del lado que manda.
      if (slot !== "main") return;
      // Promoción: lo que estaba al costado pasa al área grande. Se vuelca
      // ANTES de soltar la vista y de leer el archivo, porque `dispose()` lanza
      // su guardado pero no lo espera: sin esto, el área grande leería del
      // disco un texto más viejo que el que se acaba de ver.
      await this.flushSlot(other);
      await this.close(other);
    }

    // Desde acá ya se va a otro lado: se anota dónde quedaba el paso actual
    // ANTES de desarmar nada, que es mientras la vista todavía puede decirlo.
    if (track) this.capture(slot);

    // Un PDF no es texto: sale por su propia rama antes de intentar leerlo.
    if (isPdfPath(path)) {
      const data = await adapter.readBinary(path);
      await this.unmount(slot);
      this.enterSlot(slot, "markdown", path);
      const view = new PdfView(host, {
        title: path.split("/").pop() ?? path,
        data,
      });
      this.slots[slot] = { kind: "pdf", path, view };
      this.settle(slot, path, hash, track);
      await view.init();
      return;
    }

    // Read before tearing anything down: the disk read is async, and closing
    // first left the main area and the sidebar rail empty for that whole gap.
    const text = await adapter.read(path);

    // .md → .md: reuse the mounted view instead of remounting it. Nothing in
    // the shell changes (same layout, same rail), so there is no flash.
    //
    // Se relee el slot y no se usa lo de arriba: entre medio hubo await de
    // disco, y en ese hueco pudo entrar otro `open()`.
    const cur = this.slots[slot];
    if (!isCanvas && cur?.kind === "markdown") {
      // Reentra igual: la vista se reusa, pero el panel tiene que retitularse y
      // el teclado pasar a este lado, como en cualquier otra apertura.
      this.enterSlot(slot, "markdown", path);
      cur.view.load({
        ...this.markdownOptions(adapter, path, text, slot),
        highlights: await readHighlights(adapter, path),
      });
      this.slots[slot] = { kind: "markdown", path, view: cur.view };
      this.settle(slot, path, hash, track);
      if (hash) cur.view.revealAnchor(hash);
      return;
    }

    // Los resaltados se leen ACÁ, antes de montar nada: la hoja se arma de una
    // sola vez, con todo lo suyo puesto. Leerlos con la vista ya montada la
    // dejaría a medio vestir mientras el disco contesta.
    const highlights = isCanvas ? [] : await readHighlights(adapter, path);

    if (isCanvas) {
      await this.unmount(slot);
      this.enterSlot(slot, "canvas", path);
      const doc = fromJSONCanvas(path, text);
      const cam = await loadCamera(path);
      if (cam) doc.camera = cam;

      const store = createDocStore(doc);
      const view = new CanvasView(host, store, {
        hasKeyboard: () => this.hasKeyboard(slot),
        loadFile: (p) => adapter.read(p),
        onOpenFile: (p, { peek }) => void this.open(p, "", { slot: peek ? "peek" : slot }),
        loadBinary: (p) => adapter.readBinary(p),
        loadHighlights: (p) => readHighlights(adapter, p),
        saveImage: (blob, name) => adapter.createBinary(`${ATTACHMENTS_DIR}/${name}`, blob),
      });
      await view.init();
      // No persisted viewport (un canvas importado, o abierto en otra máquina) → frame the
      // content so cards aren't left off-screen at the origin.
      if (!cam && doc.cards.length > 0) view.fitAll();

      const entry: Entry = {
        kind: "canvas",
        path,
        view,
        store,
        unsub: () => {},
        timer: null,
        lastJson: toJSONCanvas(doc),
      };
      entry.unsub = store.subscribe(() => {
        if (entry.timer !== null) clearTimeout(entry.timer);
        entry.timer = window.setTimeout(() => {
          const d = store.getState().doc;
          const json = toJSONCanvas(d);
          if (json !== entry.lastJson) {
            entry.lastJson = json;
            void adapter.write(path, json);
          }
          void saveCamera(path, d.camera);
          entry.timer = null;
        }, SAVE_DEBOUNCE_MS);
      });
      this.slots[slot] = entry;
    } else {
      // Markdown keeps the normal full-area view; the sidebar docks into a
      // classic full-height left rail beside it.
      //
      // Primero se PONE la hoja y recién después se saca lo que había. Las dos
      // vistas ocupan el host entero y la hoja se monta después, así que queda
      // encima y opaca: lo viejo se desarma tapado y el host nunca está vacío.
      // Al revés —desmontar y después montar— hay un instante sin ninguna
      // vista, y desarmar el canvas no es gratis: soltar su contexto de WebGPU
      // deja su área en negro, que es el parpadeo que se veía al salir de un
      // canvas hacia una nota.
      const outgoing = this.slots[slot];
      this.slots[slot] = null;
      this.enterSlot(slot, "markdown", path);
      const view = new MarkdownView(host, {
        ...this.markdownOptions(adapter, path, text, slot),
        highlights,
      });
      this.slots[slot] = { kind: "markdown", path, view };
      if (hash) view.revealAnchor(hash);
      if (outgoing) await this.disposeEntry(outgoing);
    }

    this.settle(slot, path, hash, track);
  }

  /**
   * Deja asentado qué documento está abierto en grande.
   *
   * Sólo lo dice el slot principal. `activePath` es lo que marca la fila del
   * rail, lo que expande sus carpetas y lo que se recuerda como "dónde estabas"
   * al reabrir el vault: si el panel lateral lo pisara, consultar una nota al
   * costado movería la selección del árbol y el vault reabriría en ella.
   */
  private settle(slot: SlotId, path: string, hash: string, track: boolean): void {
    if (slot === "main") this.vault.getState().setActive(path);
    if (track) this.record(slot, path, hash);
  }

  // ---- rastro (ir y volver) -------------------------------------------------

  /**
   * Anota dónde quedó el paso actual, justo antes de irse de él.
   *
   * Se compara la ruta porque el paso y la vista pueden haberse desacoplado
   * (una apertura sin rastro, un archivo que se cerró solo): anotar el
   * desplazamiento de un documento en el paso de otro es peor que no anotarlo.
   */
  private capture(slot: SlotId): void {
    const trail = this.trails[slot];
    const here = trail.places[trail.at];
    const cur = this.slots[slot];
    if (!here || !cur || cur.path !== here.path) return;
    if (cur.kind === "markdown") here.scroll = cur.view.scrollTop;
  }

  /** Suma un paso al rastro de un slot. */
  private record(slot: SlotId, path: string, hash: string): void {
    const trail = this.trails[slot];
    // Ir a un lado nuevo borra lo que hubiera para adelante: el rastro es una
    // línea, no un árbol.
    trail.places.length = trail.at + 1;
    trail.places.push({ path, hash, scroll: 0 });
    const over = trail.places.length - TRAIL_MAX;
    if (over > 0) trail.places.splice(0, over);
    trail.at = trail.places.length - 1;
    this.paintTrail(slot);
  }

  /** ¿Hay un paso a −1 (volver) o a +1 (ir) desde donde se está? */
  private canGo(slot: SlotId, delta: -1 | 1): boolean {
    const trail = this.trails[slot];
    return trail.places[trail.at + delta] !== undefined;
  }

  private paintTrail(slot: SlotId): void {
    this.hosts.setTrail?.(slot, this.canGo(slot, -1), this.canGo(slot, 1));
  }

  /** ¿Se puede volver (−1) o ir (+1) en este slot? Para el shell y los atajos. */
  canTravel(slot: SlotId, delta: -1 | 1): boolean {
    return this.canGo(slot, delta);
  }

  /**
   * Volver (−1) o ir (+1) un paso en el rastro de un slot.
   *
   * El paso se confirma DESPUÉS de que la apertura salió bien. Un archivo del
   * rastro puede haber desaparecido del disco mientras tanto (lo movió otra
   * app), y mover el índice antes dejaría el rastro apuntando a un escalón que
   * no se puede pisar; así, el escalón muerto se saca y el rastro sigue sano.
   */
  async go(slot: SlotId, delta: -1 | 1): Promise<void> {
    const trail = this.trails[slot];
    const next = trail.at + delta;
    const place = trail.places[next];
    if (!place) return;
    this.capture(slot);
    try {
      await this.open(place.path, place.hash, { slot, track: false });
    } catch {
      trail.places.splice(next, 1);
      if (trail.at > next) trail.at -= 1;
      this.paintTrail(slot);
      return;
    }
    trail.at = next;
    this.restore(slot, place);
    this.paintTrail(slot);
  }

  /**
   * Vuelve al punto exacto del documento, no sólo al documento.
   *
   * Con ancla no hay nada que reponer: `open` ya llevó la hoja al encabezado que
   * nombraba el enlace, y pisarlo con el desplazamiento guardado sería
   * contestarle al enlace con el scroll de la vez anterior.
   */
  private restore(slot: SlotId, place: Place): void {
    if (place.hash || !place.scroll) return;
    const cur = this.slots[slot];
    if (cur?.kind !== "markdown") return;
    cur.view.scrollTo(place.scroll);
  }

  /**
   * Un archivo del rastro se movió: se lo reapunta en vez de perder el paso.
   *
   * `retargetPath` es el mismo que reapunta las tarjetas de un lienzo, así que
   * mover una carpeta arrastra a todo lo que tenga adentro sin contarlo acá.
   */
  retrail(from: string, to: string): void {
    for (const slot of SLOTS) {
      for (const place of this.trails[slot].places) {
        const next = retargetPath(place.path, from, to);
        if (next !== null) place.path = next;
      }
    }
  }

  /** Path of the document open in the main area, or null. */
  get activePath(): string | null {
    return this.slots.main?.path ?? null;
  }

  /** Path of the document open in the side panel, or null. */
  get peekPath(): string | null {
    return this.slots.peek?.path ?? null;
  }

  /**
   * Quién atiende el teclado.
   *
   * Lo dice el shell, que es el dueño del DOM, y lo pregunta el lienzo antes de
   * actuar sobre una tecla: sus atajos escuchan en `window`, así que sin esto
   * apretar Supr con el foco en la cabecera del panel lateral borra la
   * selección del lienzo que quedó a la izquierda.
   */
  setFocusedSlot(slot: SlotId): void {
    this.focused = slot;
  }

  /** ¿Tiene `slot` el teclado? */
  hasKeyboard(slot: SlotId): boolean {
    return this.focused === slot;
  }

  /** Dónde está trabajando el usuario: a qué lado le hablan los atajos. */
  get focusedSlot(): SlotId {
    return this.focused;
  }

  /**
   * Qué hay abierto y dónde.
   *
   * Es lo que consulta cualquiera que vaya a TOCAR un archivo del vault
   * —renombrar, borrar, reescribir enlaces—: la pregunta no es "¿es el
   * documento activo?" sino "¿lo tiene abierto alguna vista?", y desde que hay
   * más de un slot las dos dejaron de ser la misma. El arreglo es una foto, así
   * que se puede cerrar slots mientras se lo recorre.
   */
  openSlots(): { slot: SlotId; path: string }[] {
    const out: { slot: SlotId; path: string }[] = [];
    for (const slot of SLOTS) {
      const cur = this.slots[slot];
      if (cur) out.push({ slot, path: cur.path });
    }
    return out;
  }

  /** En qué slot está abierto `path`, o null si no lo tiene ninguno. */
  slotOf(path: string): SlotId | null {
    for (const slot of SLOTS) {
      if (this.slots[slot]?.path === path) return slot;
    }
    return null;
  }

  /**
   * Vuelca a disco lo que las vistas abiertas tengan pendiente, sin cerrarlas.
   *
   * Existe para quien va a LEER el vault enseguida: mientras el autoguardado
   * esté esperando su debounce, el archivo en disco es más viejo que lo que se
   * ve en pantalla, y cualquier barrido que lo lea trabajaría sobre un texto
   * que está por ser reemplazado. Se vuelcan TODOS los slots: el barrido lee el
   * vault entero, no sólo el documento grande.
   */
  async flush(): Promise<void> {
    for (const slot of SLOTS) await this.flushSlot(slot);
  }

  /** Lo mismo para un solo slot. */
  private async flushSlot(slot: SlotId): Promise<void> {
    const cur = this.slots[slot];
    if (!cur) return;
    if (cur.kind === "canvas") {
      if (cur.timer !== null) {
        clearTimeout(cur.timer);
        cur.timer = null;
      }
      const doc = cur.store.getState().doc;
      const json = toJSONCanvas(doc);
      const adapter = this.vault.getState().adapter;
      if (adapter && json !== cur.lastJson) {
        cur.lastJson = json;
        await adapter.write(cur.path, json);
      }
    } else if (cur.kind === "markdown") {
      await cur.view.flush();
    }
  }

  /**
   * A file an open canvas embeds just moved: repoint its `file` cards in place,
   * letting the debounced save carry the change to disk.
   *
   * Returns the canvas's path, which tells the caller to exclude it from the
   * on-disk sweep — the live store outranks the file, and its next autosave
   * would write the stale path straight back over it. Null cuando no hay ningún
   * canvas montado.
   *
   * Se busca por tipo y no por slot: que un lienzo sólo se abra en grande es una
   * regla de `open`, y repetirla acá la volvería dos.
   */
  retargetFileRefs(from: string, to: string): string | null {
    for (const slot of SLOTS) {
      const cur = this.slots[slot];
      if (cur?.kind !== "canvas") continue;
      const { doc, updateCard } = cur.store.getState();
      for (const card of doc.cards) {
        if (card.type !== "file" || !card.file) continue;
        const next = retargetPath(card.file, from, to);
        if (next !== null) updateCard(card.id, { file: next, updatedAt: Date.now() });
      }
      return cur.path;
    }
    return null;
  }

  /** If `path` is open in any slot, close it (e.g. after it was deleted). */
  async closeIfOpen(path: string): Promise<void> {
    const slot = this.slotOf(path);
    if (slot === null) return;
    await this.close(slot);
    // Cerrar el panel lateral no deja el área grande vacía: el cartel de vault
    // vacío y la forma de la ventana son del principal.
    if (slot !== "main") return;
    this.vault.getState().setActive(null);
    this.hosts.emptyStateText.textContent =
      `"${this.vault.getState().name}" is empty — create your first note or canvas.`;
    this.hosts.emptyState.hidden = false;
    this.hosts.setSidebarLayout("docked");
    this.hosts.setViewMode?.(null);
  }

  /**
   * Saca la vista de un slot y nada más.
   *
   * Está separado de `close()` porque relevar el contenido de un slot NO es
   * cerrarlo: al abrir un PDF o un lienzo la vista anterior se desmonta ahí
   * mismo, y si eso arrastrara lo que hace `close` —guardar el panel, devolver
   * el teclado, borrar el rastro— volver a un PDF desde el panel se llevaría
   * puesto el historial con el que se acaba de llegar.
   */
  private async unmount(slot: SlotId): Promise<void> {
    const cur = this.slots[slot];
    if (!cur) return;
    this.slots[slot] = null;
    await this.disposeEntry(cur);
  }

  /** Cierra un slot: lo desmonta y, si es el panel, lo guarda. */
  async close(slot: SlotId = "main"): Promise<void> {
    const had = this.slots[slot] !== null;
    await this.unmount(slot);
    if (slot !== "peek" || !had) return;
    this.hosts.setPeek?.(null);
    // El teclado vuelve al área grande: el panel que lo tenía ya no está.
    this.focused = "main";
    // Y el rastro se va con él: el panel es transitorio —no sobrevive a una
    // recarga— así que volver a abrirlo empieza de cero, no en el medio del
    // recorrido de la vez pasada.
    this.trails.peek = emptyTrail();
    this.paintTrail("peek");
  }

  /** Desmonta todos los slots (cerrar el vault, volver al tablero). */
  async closeAll(): Promise<void> {
    for (const slot of SLOTS) await this.close(slot);
    // Otro vault es otro recorrido: el rastro no cruza de uno al otro.
    for (const slot of SLOTS) {
      this.trails[slot] = emptyTrail();
      this.paintTrail(slot);
    }
  }

  /**
   * Desmonta una vista y deja en disco lo que tuviera pendiente.
   *
   * Está separado de `close()` porque hay un camino que cierra una vista que YA
   * no es la del slot: al pasar a markdown la hoja se monta encima, y lo de
   * abajo se desarma después.
   *
   * La vista se saca ANTES de escribir, no después: lo que se guarda ya está
   * medido (el JSON se arma arriba de todo), y mientras el canvas siga montado
   * sigue vivo —su ticker corre y sus atajos de teclado escuchan—, así que
   * dejarlo respirando durante dos escrituras a disco es dejar dos documentos
   * atendiendo el teclado al mismo tiempo.
   */
  private async disposeEntry(cur: Entry): Promise<void> {
    if (cur.kind === "canvas") {
      cur.unsub();
      // Flush any pending debounced save synchronously.
      if (cur.timer !== null) clearTimeout(cur.timer);
      const d = cur.store.getState().doc;
      const json = toJSONCanvas(d);
      cur.view.dispose();
      const adapter = this.vault.getState().adapter;
      if (adapter) {
        if (json !== cur.lastJson) await adapter.write(cur.path, json);
        await saveCamera(cur.path, d.camera);
      }
    } else {
      cur.view.dispose(); // flushes its own pending save
    }
  }
}
